import React, { useMemo } from 'react'
import { Alert, Box, Card, Button } from '@mui/material'

import { ConfirmDialog, ConfirmDialogProps } from 'shared/ui'
import { NodeView } from './tree/NodeView'
import { useDeleteLeaf, useDeleteNode } from './tree/TreeActions'
import { DownloadTreeButton } from './tree/DownloadTreeButton'
import { TreeUrlView } from './tree/TreeUrlView'
import { RecentlyCreated } from './tree/RecentlyCreated'
import { BuilderContext } from './builder/BuilderContext'
import { copyToClipboard } from 'shared/utils'
import { nodeForId, stripTreeJsonForAppend } from './utility/TreeHelpers'
import { createIdMap } from './version_mapping/utility'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { useSearchParams } from 'shared/hooks'
import { useParams } from 'infrastructure/routing'

const hideDialog = {
  title: '',
  content: '',
  open: false,
  onConfirm: () => {},
  onClose: () => {},
}

export function Builder({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const deleteNode = useDeleteNode()
  const deleteLeaf = useDeleteLeaf()

  const { tree, resetTree } = useGetTreeById({ treeId, versionId })
  const [searchParams, setSearchParams] = useSearchParams()
  const { NodeId } = useParams()
  const nodeId = searchParams.get('nodeId') || NodeId
  const [error, setError] = React.useState<string | null>()
  const [success, setSuccess] = React.useState<string | null>()
  const [currentDialog, setCurrentDialog] =
    React.useState<ConfirmDialogProps>(hideDialog)
  const [recentlyCreated, setRecentlyCreated] = React.useState<
    { name: string; url: string }[]
  >([])

  const currentNode = useMemo(() => {
    if (!nodeId || !tree) return tree
    return nodeForId(nodeId, tree)
  }, [tree, nodeId])

  const idMap = useMemo(() => {
    if (!tree) return
    return createIdMap(tree)
  }, [tree])

  const resetError = (error: string | null) => {
    setError(error)
    setSuccess(null)
  }

  const resetSuccess = (success: string | null) => {
    setSuccess(success)
    setError(null)
  }

  const currentTreeParams = React.useMemo(() => {
    if (!tree) return
    return {
      treeId: tree.tree_id,
      versionId: tree.version,
      nodeId: tree.id,
    }
  }, [tree])

  const onBeforeAction = React.useCallback(() => {
    setError(null)
    setSuccess(null)
  }, [])

  const onActionSuccess = React.useCallback((reFetch = true) => {
    resetSuccess('Success')
    if (reFetch) {
      resetTree?.()
    }
  }, [])

  const onActionError = React.useCallback((err: Error) => {
    resetError(err.toString())
  }, [])

  const deleteThisNode = (nId: string, rootId?: string) => {
    const onConfirm = () => {
      onBeforeAction()
      if (!currentTreeParams) return
      const { treeId, versionId } = currentTreeParams
      deleteNode({ treeId, versionId, nodeId: nId })
        .then(() => {
          setCurrentDialog(hideDialog)
          rootId && goToNode(rootId)
        })
        .catch(onActionError)
    }

    setCurrentDialog({
      title: 'Do The Delete For Real?',
      children: `This CANT be undone`,
      open: true,
      onConfirm,
      onClose: () => setCurrentDialog(hideDialog),
    })
  }

  const deleteChildNode = (nId: string) => {
    deleteThisNode(nId, tree!.id)
  }

  const deleteChildLeaf = (leafId: string) => {
    const onConfirm = () => {
      onBeforeAction()
      if (!currentTreeParams) return
      const { treeId, versionId, nodeId } = currentTreeParams
      return deleteLeaf({ treeId, versionId, leafId })
        .then(() => {
          setCurrentDialog(hideDialog)
          goToNode(nodeId)
        })
        .catch(onActionError)
    }

    setCurrentDialog({
      title: 'Do The Delete For Real?',
      children: `This CANT be undone`,
      open: true,
      onConfirm,
      onClose: () => setCurrentDialog(hideDialog),
    })
  }

  function goToNode(nodeId?: string) {
    if (!tree) return
    setSearchParams({ nodeId })
  }

  function onRoot() {
    return currentNode && !currentNode.parent_id
  }

  const nodeActions = [{ title: 'Delete', action: deleteChildNode }]
  const leafActions = [{ title: 'Delete', action: deleteChildLeaf }]

  const contextValue = React.useMemo(
    () => ({ onActionSuccess, onActionError, onBeforeAction }),
    [onActionSuccess, onActionError, onBeforeAction],
  )

  if (!currentNode || !idMap) {
    return <Box>Loading Tree...</Box>
  }
  return (
    <BuilderContext.Provider value={contextValue}>
      <Card>
        <ConfirmDialog {...currentDialog} />
        <TreeUrlView tree={tree} />
        <RecentlyCreated recentlyCreated={recentlyCreated} />
        <DownloadTreeButton tree={currentNode} />
        <Button
          variant="outlined"
          onClick={() => copyToClipboard(JSON.stringify(tree, null, 2))}
        >
          Copy Full JSON
        </Button>
        <Button
          variant="outlined"
          onClick={() =>
            copyToClipboard(
              JSON.stringify(stripTreeJsonForAppend(currentNode), null, 2),
            )
          }
        >
          Copy JSON For Append
        </Button>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <React.Fragment>
          <div>Tree ID: {currentNode.tree_id}</div>
          <div>Version: {currentNode.version}</div>
          <div>Node ID: {currentNode.id}</div>
          <Button
            onClick={() =>
              copyToClipboard(
                `${currentNode.tree_id}:${currentNode.version}:${currentNode.id}`,
              )
            }
          >
            Copy Ids
          </Button>
          {!onRoot() && (
            <div>
              <Button onClick={() => goToNode(currentNode.parent_id)}>
                Parent
              </Button>
              <Button onClick={() => goToNode()}>Root</Button>
            </div>
          )}
          <NodeView
            nodeActions={nodeActions}
            leafActions={leafActions}
            node={currentNode}
            onClick={goToNode}
            depth={0}
            idMap={idMap}
          />
        </React.Fragment>
      </Card>
    </BuilderContext.Provider>
  )
}
